import { useEffect, useRef, useState } from 'react';
import ClassNames from '@streamloots/classnames';
import { PageFeedComment } from 'model/indexTS';
import trackEventManager from 'utils/event-manager';
import { TranslateInterface } from 'utils/translation';
import Button, { ButtonIcon } from 'ui-library/button';
import Dialog from 'ui-library/dialog';
import { ConfirmationDialogContent } from 'components/confirmation-dialog';
import { useFeedComments } from './useFeedComments';
import theme from './feed-comments.scss';

const classNames = ClassNames(theme);

export interface FeedCommentActionsProps extends TranslateInterface {
  feedComment: PageFeedComment;
}

export const FeedCommentActions = ({ feedComment, t }: FeedCommentActionsProps): JSX.Element => {
  const { feed, isDeleting, handleDeleteComment } = useFeedComments();
  const [openOption, setOpenOptions] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);

  const openOptions = () => {
    trackEventManager.feedClicked({
      button: 'comment content menu',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });
    setOpenOptions(true);
  };
  const closeOptions = () => {
    setOpenOptions(false);
  };

  const handleDeleteConfirmOpenClick = () => {
    trackEventManager.feedClicked({
      button: 'delete comment',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });
    setOpenConfirmation(true);
    closeOptions();
  };

  const closeConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleConfirmClick = async () => {
    trackEventManager.feedClicked({
      button: 'confirm delete comment',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });
    handleDeleteComment(feedComment._id);
  };

  useEffect(() => {
    if (!isDeleting) {
      setOpenConfirmation(false);
    }
  }, [isDeleting]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (innerRef.current?.contains(event.target)) {
        return;
      }

      if (inputRef.current?.contains(event.target)) {
        return;
      }

      closeOptions();
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div ref={innerRef} className={classNames('feed-comments__actions')}>
      <ButtonIcon
        icon="dots-horizontals"
        onClick={openOptions}
        className={classNames('feed-comments__actions__button')}
        title={t('pageFeed:feedDetail.showOptions')}
        ariaLabel={t('pageFeed:feedDetail.comments.showOptions')}
      />
      <div
        ref={inputRef}
        className={classNames({
          'feed-comments__actions__options': true,
          'feed-comments__actions__options--hide': !openOption,
        })}
      >
        <Button
          label={t('pageFeed:feedDetail.comments.delete.deleteOption')}
          className={classNames('feed-comments__actions__options__item')}
          onClick={handleDeleteConfirmOpenClick}
          ariaLabel={t('pageFeed:feedDetail.comments.delete.deleteOption')}
        />
      </div>
      <Dialog
        active={openConfirmation}
        renderDialogBody={() => (
          <ConfirmationDialogContent
            onClose={closeConfirmation}
            title={t('pageFeed:feedDetail.comments.delete.deleteConfirm')}
            confirmationMessage={t('pageFeed:feedDetail.comments.delete.deleteConfirmDetail')}
            confirmButtonText={t('pageFeed:feedDetail.comments.delete.deleteConfirmButton')}
            onConfirmClick={handleConfirmClick}
            isLoading={isDeleting}
          />
        )}
      />
    </div>
  );
};
